import type { OptionChoices } from "building-types/options/optionChoices";
import type {
  MaterialLoadDiagnosticsSnapshot,
  MaterialLoadDiagnosticsState,
} from "gfx/materials/materialLoadDiagnostics";

export interface ShowroomDebugInjectedHomeFile {
  filename: string;
  body: string;
  optionChoices?: OptionChoices | null;
}

export type ShowroomIntroPerformanceMode = "default" | "worker";
export type ShowroomIntroFloorPlanBenchmarkMode = "default" | "off" | "on";
export type ShowroomIntroTownhomesMode = "default" | "off" | "on";

export interface ShowroomIntroBenchmarkMeasureEntry {
  name: string;
  startTimeMs: number;
  durationMs: number;
  endTimeMs: number;
  source: "showroomPerformance" | "debugFallback";
}

export interface ShowroomIntroBenchmarkPhaseEntry {
  readonly name: string;
  readonly startTimeMs: number;
  readonly durationMs: number;
  readonly endTimeMs: number;
  readonly source: "client" | "worker" | "server";
}

export interface ShowroomIntroBenchmarkStateSnapshot {
  buildingGenerationCount?: number;
  initialBuildingRendered?: boolean;
  mode?: ShowroomIntroPerformanceMode;
  floorPlanMode?: ShowroomIntroFloorPlanBenchmarkMode;
  townhomesMode?: ShowroomIntroTownhomesMode;
  renderMode?: "loading" | "building" | "gltf";
  gltfSource?: "worker" | "server";
  showroomWorkerBuildingGen?: boolean;
  configTownhomes?: boolean;
  modelComponents?: boolean;
  horizontalAssemblies?: boolean;
  hasFloorPlanPayload?: boolean;
  levels?: Array<{
    id: string;
    name: string;
    sortIndex: number;
    floorElevation: number;
    isGroundLevel: boolean;
  }>;
  selectedLevelId?: string;
  sceneSelectedLevelId?: string | null;
  viewMode?: string;
}

export interface ShowroomIntroBenchmarkWindow extends Window {
  higharcMaterialLoadDiagnostics?: MaterialLoadDiagnosticsState;
  showroomIntroBenchmarkPhases?: ShowroomIntroBenchmarkPhaseEntry[];
  showroomIntroBenchmarkMeasures?: Record<string, ShowroomIntroBenchmarkMeasureEntry>;
  showroomIntroBenchmarkCaptureInstalled?: boolean;
  showroomIntroBenchmarkStartTime?: number;
  showroomIntroBenchmarkObserver?: PerformanceObserver;
  showroomIntroBenchmarkState?: ShowroomIntroBenchmarkStateSnapshot;
}

export function recordShowroomIntroBenchmarkPhase(entry: ShowroomIntroBenchmarkPhaseEntry): void {
  if (typeof window === "undefined") {
    return;
  }

  // Only record when the intro-performance debug page has installed the phase array;
  // regular showroom sessions skip the bookkeeping entirely.
  const benchmarkWindow = window as ShowroomIntroBenchmarkWindow;
  benchmarkWindow.showroomIntroBenchmarkPhases?.push(entry);
}

/** Record a benchmark phase span ending now. No-op when startTimeMs is unknown. */
export function recordShowroomIntroBenchmarkPhaseToNow(
  name: string,
  source: ShowroomIntroBenchmarkPhaseEntry["source"],
  startTimeMs: number | null | undefined,
  endTimeMs: number = typeof performance === "undefined" ? 0 : performance.now(),
): void {
  if (startTimeMs === null || startTimeMs === undefined) {
    return;
  }

  recordShowroomIntroBenchmarkPhase({
    name,
    source,
    startTimeMs,
    durationMs: endTimeMs - startTimeMs,
    endTimeMs,
  });
}

export function recordShowroomIntroBenchmarkPhaseFromTiming({
  durationMs,
  name,
  source,
  startTimeMs,
}: {
  readonly durationMs: number;
  readonly name: string;
  readonly source: ShowroomIntroBenchmarkPhaseEntry["source"];
  readonly startTimeMs?: number;
}): void {
  if (startTimeMs === undefined) {
    return;
  }

  const timeOrigin = typeof performance === "undefined" ? 0 : performance.timeOrigin;
  const relativeStartTimeMs = startTimeMs - timeOrigin;
  recordShowroomIntroBenchmarkPhase({
    name,
    source,
    startTimeMs: relativeStartTimeMs,
    durationMs,
    endTimeMs: relativeStartTimeMs + durationMs,
  });
}

export interface ShowroomIntroBenchmarkTimelineMarker {
  readonly name:
    | "loadStarted"
    | "docReady"
    | "buildingReady"
    | "optionsReady"
    | "timeTo3dInteractive"
    | "floorPlanReady";
  readonly timeMs: number;
  readonly source: "benchmark" | "showroomPerformance" | "debugFallback";
}

export interface ShowroomIntroBenchmarkCanvasRenderSnapshot {
  readonly width: number;
  readonly height: number;
  readonly clientWidth: number;
  readonly clientHeight: number;
  readonly dataUrlLength: number | null;
}

export interface ShowroomIntroBenchmarkFinalRenderSnapshot {
  readonly canvasCount: number;
  readonly visibleCanvasCount: number;
  readonly renderedCanvasCount: number;
  readonly canvases: ShowroomIntroBenchmarkCanvasRenderSnapshot[];
  readonly hasOptionJourney: boolean;
  readonly hasStatsText: boolean;
}

export interface ShowroomIntroBenchmarkPageDiagnostics {
  readonly pageErrors: string[];
  readonly consoleWarnings: string[];
  readonly consoleErrors: string[];
  readonly failedRequests: string[];
  readonly badResponses: string[];
}

export interface ShowroomIntroBenchmarkSampleDiagnostics extends ShowroomIntroBenchmarkPageDiagnostics {
  readonly status: "ok" | "suspect";
  readonly warnings: string[];
  readonly statusText: string | null;
  readonly finalUrl: string;
  readonly benchmarkState: ShowroomIntroBenchmarkStateSnapshot | null;
  readonly finalRender: ShowroomIntroBenchmarkFinalRenderSnapshot | null;
}

export interface ShowroomIntroBenchmarkSampleMetrics {
  readonly timeTo3dInteractiveMs: number;
  readonly performanceMeasures: Record<string, number>;
  readonly timeline: ShowroomIntroBenchmarkTimelineMarker[];
  readonly diagnostics: ShowroomIntroBenchmarkSampleDiagnostics;
  readonly benchmarkPhases?: ShowroomIntroBenchmarkPhaseEntry[] | null;
  readonly materialLoadDiagnostics?: MaterialLoadDiagnosticsSnapshot | null;
}

export interface RunShowroomIntroBenchmarkSampleOptions<BrowserType = unknown> {
  readonly authEmail?: string;
  readonly baseUrl?: string;
  readonly browser: BrowserType;
  readonly browserConsole?: boolean;
  readonly floorPlanMode?: ShowroomIntroFloorPlanBenchmarkMode;
  readonly homeFile: string;
  readonly mode: ShowroomIntroPerformanceMode;
  readonly size?: string;
}

export interface ShowroomDebugFrameSize {
  width: number;
  height: number;
}

export const SHOWROOM_DEBUG_HOME_FILE_STORAGE_KEY = "__showroom_debug_injected_home_file";

export const SHOWROOM_DEBUG_HOME_FILE_QUERY_PARAM = "__showroomDebugInjectedHomeFile";

export function parseShowroomDebugFrameSize(
  sizeParam: string | undefined,
  defaultSize: ShowroomDebugFrameSize,
): ShowroomDebugFrameSize {
  if (!sizeParam) {
    return defaultSize;
  }

  const match = /^(\d{2,4})x(\d{2,4})$/.exec(sizeParam);
  if (!match) {
    return defaultSize;
  }

  return {
    width: Number(match[1]),
    height: Number(match[2]),
  };
}

export function consumeInjectedShowroomHomeFile(
  storageKey: string = SHOWROOM_DEBUG_HOME_FILE_STORAGE_KEY,
): ShowroomDebugInjectedHomeFile | null {
  if (typeof window === "undefined") {
    return null;
  }

  const rawPayload = window.sessionStorage.getItem(storageKey);
  if (!rawPayload) {
    return null;
  }

  window.sessionStorage.removeItem(storageKey);

  try {
    const parsed = JSON.parse(rawPayload) as Partial<ShowroomDebugInjectedHomeFile>;
    if (typeof parsed.filename !== "string" || typeof parsed.body !== "string") {
      return null;
    }

    const injectedHomeFile: ShowroomDebugInjectedHomeFile = {
      filename: parsed.filename,
      body: parsed.body,
    };
    if (parsed.optionChoices !== undefined) {
      injectedHomeFile.optionChoices = parsed.optionChoices;
    }
    return injectedHomeFile;
  } catch {
    return null;
  }
}
